/**
 * New tenant page — provisioning wizard step 1 (basics).
 *
 * Form fields per the plan: company name, slug, primary domain,
 * timezone, locale. Slug is auto-derived from the name until the
 * operator manually edits it; thereafter it stays put.
 *
 * Server errors:
 *   - 409 slug_taken → inline error under the slug field
 *   - 400 validation_error → top banner + best-effort field error
 *   - other → top banner with the message
 *
 * On 201 the operator is redirected to /fleet so they see the new
 * row appear (the Fleet query is invalidated to force a refetch).
 */

import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';
import { deriveSlug } from '../lib/slug';
import { TIMEZONE_OPTIONS, OTHER_TIMEZONE_SENTINEL, LOCALE_OPTIONS } from '../lib/timezones';

interface CreateTenantBody {
  name: string;
  slug: string;
  timezone: string;
  primaryDomain?: string | null;
  locale: string;
}

interface CreateTenantResponse {
  data: {
    tenant: { id: string; name: string; slug: string };
  };
}

interface ApiError {
  error: string;
  message: string;
  details?: Array<{ path: (string | number)[]; message: string }>;
}

async function postTenant(body: CreateTenantBody): Promise<CreateTenantResponse> {
  const token = getStoredToken();
  const res = await fetch(apiPath('/api/provisioning/tenants'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Throw the parsed error so React Query's onError gets the shape.
    let parsed: ApiError | null = null;
    try {
      parsed = (await res.json()) as ApiError;
    } catch {
      // ignore — fall through to generic error
    }
    const err = new Error(parsed?.message ?? `request failed: ${res.status}`) as Error & {
      status?: number;
      apiError?: ApiError | null;
    };
    err.status = res.status;
    err.apiError = parsed;
    throw err;
  }
  return res.json();
}

export default function NewTenant() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [timezoneSelect, setTimezoneSelect] = useState<string>('America/Los_Angeles');
  const [otherTimezone, setOtherTimezone] = useState<string>('');
  const [locale, setLocale] = useState<string>('en');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const effectiveSlug = useMemo(() => {
    return slugManuallyEdited ? slug : deriveSlug(name);
  }, [name, slug, slugManuallyEdited]);

  const effectiveTimezone =
    timezoneSelect === OTHER_TIMEZONE_SENTINEL ? otherTimezone.trim() : timezoneSelect;

  const mutation = useMutation({
    mutationFn: postTenant,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      // Land on the new tenant's detail page so the operator can
      // continue the wizard (assign plan, connect Slack, etc.) without
      // hunting for the row on Fleet.
      navigate(`/fleet/${res.data.tenant.id}`);
    },
    onError: (err: Error & { status?: number; apiError?: ApiError | null }) => {
      // Translate known error shapes into field-level errors.
      const next: Record<string, string> = {};
      if (err.status === 409 && err.apiError?.error === 'slug_taken') {
        next.slug = `Slug "${effectiveSlug}" is already in use. Try another.`;
      } else if (err.status === 400 && err.apiError?.details) {
        for (const issue of err.apiError.details) {
          const key = String(issue.path[0] ?? '');
          if (key) next[key] = issue.message;
        }
      }
      setFieldErrors(next);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    // Client-side guards. Server validates everything too, but failing
    // fast keeps the spinner short and the error inline.
    const localErrors: Record<string, string> = {};
    if (!name.trim()) localErrors.name = 'Required';
    if (!effectiveSlug) localErrors.slug = 'Required';
    if (!effectiveTimezone) localErrors.timezone = 'Required';
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    mutation.mutate({
      name: name.trim(),
      slug: effectiveSlug,
      timezone: effectiveTimezone,
      primaryDomain: primaryDomain.trim() === '' ? null : primaryDomain.trim(),
      locale,
    });
  }

  const submitting = mutation.isPending;
  const topError =
    mutation.isError && !Object.keys(fieldErrors).length
      ? mutation.error.message
      : null;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">New tenant</h1>
        <p className="mt-1 text-sm text-slate-600">
          Provisioning wizard · Step 1 of 5 — Basics
        </p>
      </div>

      {topError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {topError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 rounded-md border border-slate-200 bg-white p-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
            Company name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corporation"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            required
          />
          {fieldErrors.name && <p className="mt-1 text-xs text-red-700">{fieldErrors.name}</p>}
        </div>

        {/* Slug */}
        <div>
          <label htmlFor="slug" className="block text-sm font-medium text-slate-700">
            Slug
          </label>
          <input
            id="slug"
            type="text"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugManuallyEdited(true);
              setSlug(e.target.value);
            }}
            placeholder="acme"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$"
            required
          />
          <p className="mt-1 text-xs text-slate-500">
            Lowercase letters, numbers, and hyphens. Used in URLs and exports. Auto-suggested from
            the name — edit to override.
          </p>
          {fieldErrors.slug && <p className="mt-1 text-xs text-red-700">{fieldErrors.slug}</p>}
        </div>

        {/* Primary domain */}
        <div>
          <label htmlFor="primaryDomain" className="block text-sm font-medium text-slate-700">
            Primary domain <span className="text-slate-400">(optional)</span>
          </label>
          <input
            id="primaryDomain"
            type="text"
            value={primaryDomain}
            onChange={(e) => setPrimaryDomain(e.target.value)}
            placeholder="acme.com"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {fieldErrors.primaryDomain && (
            <p className="mt-1 text-xs text-red-700">{fieldErrors.primaryDomain}</p>
          )}
        </div>

        {/* Timezone */}
        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-slate-700">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezoneSelect}
            onChange={(e) => setTimezoneSelect(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
            <option value={OTHER_TIMEZONE_SENTINEL}>Other (IANA name)…</option>
          </select>
          {timezoneSelect === OTHER_TIMEZONE_SENTINEL && (
            <input
              type="text"
              value={otherTimezone}
              onChange={(e) => setOtherTimezone(e.target.value)}
              placeholder="e.g. Pacific/Auckland"
              className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            />
          )}
          {fieldErrors.timezone && (
            <p className="mt-1 text-xs text-red-700">{fieldErrors.timezone}</p>
          )}
        </div>

        {/* Locale */}
        <div>
          <label htmlFor="locale" className="block text-sm font-medium text-slate-700">
            Locale
          </label>
          <select
            id="locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {LOCALE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">Affects date / number formatting in the tenant portal.</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/fleet')}
            disabled={submitting}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create tenant'}
          </button>
        </div>
      </form>
    </div>
  );
}
